import { elide } from "@common/utils/activationSuiteDisplay"
import { normaliseClass } from "../../summer/constants/summerContent"
import {
    FUNNEL_BESOINS,
    toggleBesoinInList,
} from "@data/constants/funnelBesoinsConstants"
import { weekDaysObject } from "@data/constants/activitiesConstants"
import { formatNameOfOffer } from "./inscriptionFormats"

/**
 * Contenu du funnel Inscription.
 *
 * Les OPTIONS ne sont pas recopiées : les chips « spécificités » viennent de la
 * définition partagée, les crans d'objectif du commun des funnels. Seul le
 * DISCOURS propre à ce funnel vit ici.
 *
 * Règle de rédaction : AUCUNE formulation datée. Ce parcours se vend en
 * septembre comme en janvier ou en avril — toute mention d'une saison, d'un
 * mois ou de « cette année » vieillit mal et sonne faux au parent qui arrive
 * en cours d'année.
 */

export { normaliseClass }

// Le libellé des créneaux vit avec la grille (`src/data/utility/constraintsGrid`),
// que les ports de l'espace parent lisent aussi.
export { formatSlotsLabel } from "@data/utility/constraintsGrid"

/**
 * Les disponibilités dites comme le parent les dirait, pour la recherche des
 * masteurs : "Lundi 17h · Jeudi 18h" — un jour et l'heure de début, une fois
 * par jour, les heures qui suivent étant sous-entendues.
 */
export const formatSlotsShort = (slots = []) => {
    const seen = new Set()
    const parts = []
    slots.forEach((slot) => {
        const [day, range] = String(slot).split(" ")
        const label = weekDaysObject[day]
        if (!label || seen.has(day)) return
        seen.add(day)
        const start = String(range || "").split("-")[0]
        parts.push(start ? `${label} ${start}` : label)
    })
    return parts.join(" · ")
}

// La question parle de « spécificités » : la chip qui les nie s'accorde au
// féminin. Le reste de la liste est celui des autres funnels.
export const INSCRIPTION_BESOINS = FUNNEL_BESOINS.map((besoin) =>
    besoin.id === "aucun" ? { ...besoin, label: "Aucune" } : besoin
)
export { toggleBesoinInList }

/**
 * Numérotation des étapes, source UNIQUE de la progression segmentée.
 *
 * Elle vit ici et non dans chaque écran : la séquence a déjà changé deux fois
 * (ajout de la configuration et du checkout, puis retrait du niveau global au
 * profit de l'objectif par matière), et un écran qui porterait son propre
 * total ferait reculer la progression d'une étape à l'autre.
 */
export const INSCRIPTION_STEPS = {
    class: 1,
    besoins: 2,
    configuration: 3,
    tutors: 4,
    checkout: 5,
    total: 5,
}

/**
 * Titres des étapes et de leurs questions — déseasonnalisés, et à
 * l'affirmative : un titre qui dit ce qu'on règle se passe de sous-titre, là
 * où une question en appelait un.
 *
 * Ils divergent volontairement de l'été, qui les datait. Ici on parle de la
 * SCOLARITÉ, pas du moment de l'année : le même titre vaut en septembre et en
 * février. Aucun ne présuppose le genre de l'élève.
 *
 * Le niveau global n'est plus demandé : le parent dit, matière par matière,
 * où il veut aller — c'est le curseur du compositeur.
 */
export const INSCRIPTION_QUESTIONS = {
    class: (firstname) => `La classe ${elide(firstname)}`,
    besoins: (firstname) => `Les spécificités ${elide(firstname)}`,
    composer: (firstname) => `Composez la formule ${elide(firstname)}`,
    goals: "Objectif par matière",
    slots: (firstname) => `Disponibilités ${elide(firstname)}`,
    subjects: "Matières",
    hours: "Heures par semaine",
    start: "Début des cours",
    format: "Format de cours",
}

// Deux phrases que plusieurs écrans disent : la règle du lycée (le compositeur
// sous ses heures, la page du format quand un choix ne la tient pas) et le
// parcours perdu (tout écran qui écrit la formule).
export const INSCRIPTION_LYCEE_RULE =
    "Au lycée, une matière = une heure par semaine au moins."
export const INSCRIPTION_FUNNEL_LOST =
    "Votre parcours n'est plus en mémoire. Rouvrez le lien reçu par email pour reprendre là où vous en étiez."

/**
 * Sous-titre de l'étape masteurs — dit QUI attribue.
 *
 * Sans lui, le parent cherchait comment choisir un masteur pour valider :
 * la page montre des visages, donc il croit devoir en désigner un. La phrase
 * lève ce malentendu à l'endroit où il naît, sous le compte.
 */
export const INSCRIPTION_TUTORS_SUBTITLE = (firstname) =>
    `Le masteur le plus adapté pour ${firstname} lui sera attribué avant le premier cours.`

/** Sous-titre de l'étape spécificités — rassure sur l'usage des réponses. */
export const INSCRIPTION_BESOINS_HELPER = "Dys, TDAH, HPI… Optionnel, et privé."

/**
 * Ce qui se passe après la réservation.
 *
 * Le choix des créneaux n'y figure plus : il est fait à l'étape configuration.
 * Promettre au parent une étape qu'il vient de franchir donnerait l'impression
 * que sa saisie n'a pas été prise en compte.
 */
export const INSCRIPTION_NEXT_STEPS = [
    "Nous attribuons le masteur qui correspond au profil de votre enfant, sur les créneaux que vous avez choisis",
    "Vous recevez la confirmation de son planning par email",
    "Les séances démarrent à la date que vous avez choisie",
]

/**
 * L'ancien nom de chaque offre. La réservation le rappelle entre parenthèses
 * après le nom de gamme : c'est celui que le parent a lu partout ailleurs, et
 * au moment de payer il doit reconnaître ce qu'il achète.
 */
const FORMER_OFFER_NAMES = {
    essential: "cours partagé",
    integral: "cours particulier",
}

/** « Masteur Tandem (cours partagé) ». */
export const inscriptionFormatLabel = (offerType) => {
    const name = formatNameOfOffer(offerType)
    const formerName = FORMER_OFFER_NAMES[offerType]
    return formerName ? `${name} (${formerName})` : name
}

/**
 * Ce que le parent garde comme liberté de partir, selon ce qu'il a signé.
 *
 * ⚠️ Chacune ne vaut que pour SON contrat. Engagé, c'est le PREMIER MOIS qui
 * est libre : écrire « résiliable » serait faux dès le deuxième. Sans
 * engagement, « premier mois sans engagement » laisserait croire que la liberté
 * s'arrête après un mois. `short` est le libellé de la rangée visible.
 */
const EXIT_GUARANTEE = {
    committed: {
        icon: "shield-alt",
        label: "Premier mois sans engagement",
        short: "1er mois sans engagement",
        subtitle:
            "Vous gardez la main et choisissez de poursuivre au-delà du premier mois.",
    },
    free: {
        icon: "shield-alt",
        label: "Résiliable chaque mois",
        short: "Résiliable chaque mois",
        subtitle:
            "Sans frais ni préavis : la résiliation prend effet à la fin du mois en cours.",
    },
}

/**
 * Toutes les garanties de la réservation, dans l'ordre du volet. Les trois
 * marquées `short` sont celles que l'écran montre sans qu'on ouvre rien : ce
 * qui lève le doute au moment de payer, c'est de pouvoir partir, changer,
 * suspendre.
 *
 * Aucune ne promet de remboursement : le premier cours « satisfait ou
 * remboursé » ne se vend plus.
 */
export const inscriptionGuarantees = (withCommitment) => [
    {
        icon: "hand-holding-usd",
        label: "Pourquoi 1 € aujourd'hui ?",
        subtitle:
            "Cet euro réserve la place et les créneaux de votre enfant. Le reste n'est prélevé qu'au démarrage des cours.",
    },
    {
        icon: "calendar-times",
        label: "Annulation avant le début des cours",
        subtitle:
            "Tant que les cours n'ont pas commencé, vous annulez sans condition.",
    },
    withCommitment ? EXIT_GUARANTEE.committed : EXIT_GUARANTEE.free,
    {
        icon: "sliders-h",
        label: "Formule modifiable",
        short: "Formule modifiable",
        subtitle: "Heures, matières et rythme évoluent, sur simple demande.",
    },
    {
        icon: "pause-circle",
        label: "Pause jusqu'à 3 mois",
        short: "Pause jusqu'à 3 mois",
        subtitle: "Depuis l'espace parent.",
    },
    {
        icon: "users",
        label: "Valable pour la famille",
        subtitle: "Transférable à un frère ou une sœur, sur demande.",
    },
    {
        icon: "book-open",
        label: "Offre Autonomie à 19 €/mois",
        subtitle: "Les contenus, et 1 h de cours par mois à la demande.",
    },
]

/** Les trois garanties visibles : `{ icon, label }`, libellé court. */
export const inscriptionHeadlineGuarantees = (withCommitment) =>
    inscriptionGuarantees(withCommitment)
        .filter((guarantee) => guarantee.short)
        .map(({ icon, short }) => ({ icon, label: short }))

/** Ce que le parent fera lui-même, une fois inscrit, depuis l'espace parent. */
export const INSCRIPTION_PARENT_SPACE_GESTURES = [
    { icon: "comment-dots", label: "Écrire au tuteur" },
    { icon: "calendar-alt", label: "Décaler une séance" },
    { icon: "chart-line", label: "Lire les bilans" },
    { icon: "sliders-h", label: "Gérer l'abonnement" },
]

/**
 * La FAQ du funnel est servie une fois, à l'entrée, quel que soit l'engagement.
 * Le serveur marque `commitment_only` les questions qui parlent des 12 mois :
 * sans engagement on les retire, elles répondraient à un contrat que le parent
 * n'a pas choisi. Une question sans la marque se montre toujours.
 */
export const inscriptionFaqFor = (faqs, withCommitment) =>
    (faqs || []).filter((item) => withCommitment || !item.commitment_only)

/**
 * Quand part le premier prélèvement, tel que le devis serveur le date : « le 9
 * octobre » quand il attend le jour choisi, sinon le premier cours (le devis
 * n'a alors pas de date, y compris un devis gravé avant qu'on demande le jour).
 */
export const firstChargeWhen = (quote, atFirstSession) =>
    quote?.first_charge_on_label
        ? `le ${quote.first_charge_on_label}`
        : atFirstSession
